import { debounce } from 'lodash'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'

import { Search as SearchIcon } from '@mui/icons-material'
import {
  Box,
  InputAdornment,
  MenuItem,
  Button as MuiButton,
  Paper,
  Stack,
  TextField,
} from '@mui/material'
import { GridColumns, GridRenderCellParams } from '@mui/x-data-grid-pro'

import { DataTable } from '@components/tables'

import { Member, Site, SuperAdmin } from '@services/api'
import { doBatchImportMultiStepProtocols } from '@state/reducers/acqProtocolReducer'
import { enqueueSnackbar } from '@state/reducers/alertReducer'
import {
  doDeleteMultistepProtocol,
  doFindMultistepProtocols,
} from '@state/reducers/multistepProtocolReducer'
import { useSelector } from '@state/store'

import { DeleteConfirmationDialog } from '@components/dialogs/DeleteConfirmationDialog'
import { useAppDispatch } from '@hooks/reduxHooks'
import { UploadIcon } from 'lucide-react'

interface SearchBarProps {
  sites: Site[]
  selectedSiteId: number
  onSiteChange: (siteId: number) => void
  onSearchChange: (value: string) => void
  user?: Member | SuperAdmin
  onImport: (file: File) => void
}

const SearchBar = ({
  sites,
  selectedSiteId,
  onSiteChange,
  onSearchChange,
  user,
  onImport,
}: SearchBarProps) => {
  const intl = useIntl()

  return (
    <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
      <TextField
        select
        label="Site"
        value={selectedSiteId}
        onChange={(e) => onSiteChange(Number(e.target.value))}
        sx={{
          width: 220,
          '& .MuiOutlinedInput-root': {
            backgroundColor: '#fff',
            borderRadius: 2,
          },
        }}
        size="small"
      >
        <MenuItem value={-1}>Tous les sites</MenuItem>
        {sites.map((site) => (
          <MenuItem key={site.id} value={site.id}>
            {site.label}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        label={intl.formatMessage({ id: 'search' })}
        placeholder="Rechercher un protocole..."
        onChange={(e) => onSearchChange(e.target.value)}
        sx={{
          flex: 1,
          '& .MuiOutlinedInput-root': {
            backgroundColor: '#fff',
            borderRadius: 2,
          },
        }}
        size="small"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
            </InputAdornment>
          ),
        }}
      />
      {user?.account.role === 'super-admin' && (
        <MuiButton
          variant="contained"
          color="info"
          size="small"
          startIcon={<UploadIcon size={16} />}
          component="label"
          disabled={selectedSiteId === -1}
        >
          {intl.formatMessage({ id: 'import-csv' })}
          <input
            type="file"
            hidden
            accept=".csv"
            onChange={(e) => {
              const file = e.target.files?.[0]
              if (file) {
                onImport(file)
              }
              e.target.value = ''
            }}
          />
        </MuiButton>
      )}
    </Stack>
  )
}

export default function MultistepProtocols() {
  const dispatch = useAppDispatch()
  const intl = useIntl()

  const [searchValue, setSearchValue] = useState('')
  const [selectedRows, setSelectedRows] = useState<number[]>([])
  const [selectedSiteId, setSelectedSiteId] = useState(-1)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)

  const { user, multistepProtocols, loading } = useSelector(
    ({ auth, multistepProtocol, loading }) => ({
      user: auth.user,
      multistepProtocols: multistepProtocol.multistepProtocols,
      loading: loading['multistepProtocol/findAll'],
    }),
  )

  const sites = (user as unknown as { sites: Site[] })?.sites || []

  const filteredProtocols = useMemo(() => {
    if (!searchValue) return multistepProtocols
    const lower = searchValue.toLowerCase()
    return multistepProtocols.filter((p) =>
      p.code?.toLowerCase().includes(lower),
    )
  }, [multistepProtocols, searchValue])

  const debouncedSearch = useMemo(
    () => debounce((value: string) => setSearchValue(value), 300),
    [],
  )

  useEffect(() => {
    return () => {
      debouncedSearch.cancel()
    }
  }, [debouncedSearch])

  const onRefresh = useCallback(() => {
    dispatch(
      doFindMultistepProtocols({
        search: searchValue || undefined,
        siteId: selectedSiteId,
      }),
    )
  }, [dispatch, searchValue, selectedSiteId])

  const handleImport = useCallback(
    async (file: File, siteId: number) => {
      try {
        await dispatch(
          doBatchImportMultiStepProtocols({ file, siteId }),
        ).unwrap()
        dispatch(
          enqueueSnackbar({
            message: intl.formatMessage({ id: 'importSuccess' }),
            type: 'success',
          }),
        )
        onRefresh()
      } catch (error: any) {
        dispatch(
          enqueueSnackbar({
            message:
              error?.message || intl.formatMessage({ id: 'importError' }),
            type: 'error',
          }),
        )
      }
    },
    [dispatch, intl, onRefresh],
  )

  useEffect(() => {
    onRefresh()
  }, [onRefresh])

  const columns: GridColumns = useMemo(
    () => [
      {
        field: 'code',
        headerName: intl.formatMessage({ id: 'code' }),
        width: 150,
        renderCell: (params: GridRenderCellParams) => params.row.code,
      },
      {
        field: 'exam',
        headerName: intl.formatMessage({ id: 'exam' }),
        flex: 2,
        minWidth: 200,
        renderCell: (params: GridRenderCellParams) => {
          const { exams } = params.row
          return exams?.map((exam) => exam.exam.label).join(', ')
        },
      },
    ],
    [intl],
  )

  const handleDeleteClick = useCallback(() => {
    if (selectedRows.length > 0) {
      setDeleteDialogOpen(true)
    } else {
      dispatch(
        enqueueSnackbar({
          message: intl.formatMessage({ id: 'noExamSelected' }),
          type: 'error',
        }),
      )
    }
  }, [dispatch, intl, selectedRows.length])

  const handleDeleteConfirm = useCallback(async () => {
    for (const id of selectedRows) {
      await dispatch(doDeleteMultistepProtocol(id))
    }
    setDeleteDialogOpen(false)
    setSelectedRows([])
    onRefresh()
  }, [dispatch, selectedRows, onRefresh])

  return (
    <Box sx={{ height: '95%', display: 'flex', flexDirection: 'column' }}>
      <SearchBar
        sites={sites}
        selectedSiteId={selectedSiteId}
        onSiteChange={setSelectedSiteId}
        onSearchChange={debouncedSearch}
        user={user}
        onImport={(file) => handleImport(file, selectedSiteId)}
      />

      <Paper
        elevation={1}
        sx={{
          flex: 1,
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'grey.200',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <DataTable
          rows={filteredProtocols}
          columns={columns}
          loading={loading}
          detailPanelHeight={400}
          toolBarProps={{
            title: 'multistepProtocols',
            icon: 'fa fa-layer-group',
            onDelete: handleDeleteClick,
            onRefresh,
          }}
        />
      </Paper>

      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteConfirm}
        title="Confirmer la suppression"
        description={`Êtes-vous sûr de vouloir supprimer ${selectedRows.length} protocole(s) ?`}
      />
    </Box>
  )
}
